import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Sidebar from './components/Sidebar';
import MobileHeader from './components/MobileHeader';
import MobileBottomNav from './components/MobileBottomNav';
import HomeView from './views/HomeView';
import CoursesView from './views/CoursesView';
import PracticeView, { type SubView } from './views/PracticeView';
import ProfileView from './views/ProfileView';
import NotificationSidebar from './components/NotificationSidebar';
import SubscriptionView from './views/SubscriptionView';
import LandingPage from './views/LandingPage';
import InstructorDashboardView from './views/InstructorDashboardView';
import AuthModal from './components/AuthModal';
import InstructorRegistrationModal from './components/InstructorRegistrationModal';
import { useRealTimeMessages } from './hooks/useRealTimeMessages';
import { X, Users, Loader2, Play } from 'lucide-react';
import { normalizeAjaxUrl } from './utils/normalizeUrl';

type ViewType = 'হোম' | 'কোর্সসমূহ' | 'চর্চাঘর' | 'প্রোফাইল' | 'সাবস্ক্রিপশন' | 'শিক্ষক ড্যাশবোর্ড';

const viewComponents: Record<ViewType, React.ComponentType<any>> = {
  'হোম': HomeView,
  'কোর্সসমূহ': CoursesView,
  'চর্চাঘর': PracticeView,
  'প্রোফাইল': ProfileView,
  'সাবস্ক্রিপশন': SubscriptionView,
  'শিক্ষক ড্যাশবোর্ড': InstructorDashboardView,
};

const viewSlugMap: Record<ViewType, string> = {
  'হোম': '',
  'কোর্সসমূহ': 'courses',
  'চর্চাঘর': 'chorcaghor',
  'প্রোফাইল': 'profile',
  'সাবস্ক্রিপশন': 'subscription',
  'শিক্ষক ড্যাশবোর্ড': 'instructor-dashboard',
};

const subViewSlugMap: Record<SubView, string> = {
  'main': '',
  'chapterWise': 'chapter-wise',
  'mockTests': 'mock-tests',
  'questionBanks': 'question-banks',
  'schoolQuestionBanks': 'school-question-banks',
  'history': 'history',
  'user': 'user',
  'exams': 'exams',
};

const getViewFromSlug = (slug: string): ViewType => {
  for (const [view, s] of Object.entries(viewSlugMap)) {
    if (s === slug) return view as ViewType;
  }
  return 'হোম';
};

const getSubViewFromSlug = (slug: string): SubView => {
  for (const [sub, s] of Object.entries(subViewSlugMap)) {
    if (s === slug) return sub as SubView;
  }
  return 'main';
};

const parsePathname = (pathname: string): { view: ViewType; subView: SubView } => {
  const segments = pathname.replace(/^\/|\/$/g, '').split('/').filter(Boolean);
  if (segments[0] !== 'dashboard') {
    return { view: 'হোম', subView: 'main' };
  }
  const viewSlug = segments[1] || '';
  const subViewSlug = segments[2] || '';
  return {
    view: getViewFromSlug(viewSlug),
    subView: getSubViewFromSlug(subViewSlug)
  };
};

const getPathFromView = (view: ViewType, subView: SubView): string => {
  const slug = viewSlugMap[view];
  let path = '/dashboard/';
  if (slug) {
    path += slug + '/';
  }
  if (view === 'চর্চাঘর' && subView !== 'main') {
    const subViewSlug = subViewSlugMap[subView];
    if (subViewSlug) {
      path += subViewSlug + '/';
    }
  }
  return path;
};

export default function App() {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    const isWp = typeof window !== 'undefined' && !!(window as any).shAuth;
    if (isWp) {
      const hasWpUser = !!(window as any).shAuth?.user;
      if (!hasWpUser) {
        if (typeof window !== 'undefined') {
          localStorage.setItem('isAuthenticated', 'false');
        }
        return false;
      }
      return true;
    }
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [isAuthChecking, setIsAuthChecking] = useState<boolean>(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState<boolean>(false);
  const [isInstructorRegOpen, setIsInstructorRegOpen] = useState<boolean>(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);

  const handleLogout = () => {
    setIsLogoutModalOpen(true);
  };

  const handleConfirmLogout = async () => {
    setIsLoggingOut(true);
    setIsAuthChecking(true); // Lock screen during logout navigation to prevent any UI flashing
    
    const shAuth = (window as any).shAuth;
    const ajaxUrl = shAuth?.ajax_url || 'https://shuddhoshiksha.com/wp-admin/admin-ajax.php';
    const nonce = shAuth?.nonce || '';

    // Clear local storage and cookies synchronously
    setIsAuthenticated(false);
    localStorage.setItem('isAuthenticated', 'false');
    localStorage.removeItem('cw_chapter_progress');
    localStorage.removeItem('chorcaghor_favorites');
    localStorage.removeItem('subscriptionTier');



    try {
      const formData = new FormData();
      formData.append('action', 'sh_ajax_logout');
      formData.append('nonce', nonce);

      const response = await fetch(ajaxUrl, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      if (result.success) {
        const redirectUrl = result.data?.redirect || '/';
        const separator = redirectUrl.includes('?') ? '&' : '?';
        window.location.href = `${redirectUrl}${separator}logged_out=1&t=${Date.now()}`;
      } else {
        throw new Error('Logout failed');
      }
    } catch (err) {
      // Fallback in case of server/network issues
      window.location.href = `/?logged_out=1&t=${Date.now()}`;
    } finally {
      setIsLoggingOut(false);
      setIsLogoutModalOpen(false);
    }
  };

  const [activeView, setActiveView] = useState<ViewType>(() => {
    if (typeof window === 'undefined') return 'হোম';
    return parsePathname(window.location.pathname).view;
  });
  const [practiceSubView, setPracticeSubView] = useState<SubView>(() => {
    if (typeof window === 'undefined') return 'main';
    return parsePathname(window.location.pathname).subView;
  });
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean>(false);

  // Monitor screen width to auto-switch to MobileHomeView on mobile screens
  const [isMobile, setIsMobile] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll to top on view or sub-view transitions
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [activeView, practiceSubView]);

  // Initialize Socket.io messages hook
  const { notifications, notices, unreadCount, markAsRead } = useRealTimeMessages();

  // Global subscription tier state — read from backend-injected shAuth
  const [subscriptionTier, setSubscriptionTier] = useState<'free' | 'pro' | 'max'>(() => {
    const shAuth = (window as any).shAuth;
    const backendTier = shAuth?.user?.tier;
    if (backendTier === 'pro' || backendTier === 'max') {
      return backendTier;
    }
    // Fallback to localStorage for in-session upgrades
    const stored = localStorage.getItem('subscriptionTier');
    if (stored === 'pro' || stored === 'max') {
      return stored as 'pro' | 'max';
    }
    return 'free';
  });

  useEffect(() => {
    localStorage.setItem('subscriptionTier', subscriptionTier);
  }, [subscriptionTier]);

  const [incomingChallenge, setIncomingChallenge] = useState<any | null>(null);
  const [respondingChallenge, setRespondingChallenge] = useState(false);

  useEffect(() => {
    const shAuth = (window as any).shAuth;
    if (!shAuth || !shAuth.user) return;

    const ajaxUrl = normalizeAjaxUrl(shAuth.ajax_url || '/wp-admin/admin-ajax.php');
    
    const pollInvites = async () => {
      try {
        const formData = new FormData();
        formData.append('action', 'sh_poll_battle_invitations');
        
        const response = await fetch(ajaxUrl, {
          method: 'POST',
          body: formData
        });
        const res = await response.json();
        if (res.success && res.data) {
          const isCurrentlyPlaying = window.location.search.includes('battle=');
          if (!isCurrentlyPlaying && (!incomingChallenge || incomingChallenge.room_id !== res.data.room_id)) {
            setIncomingChallenge(res.data);
          }
        }
      } catch {
        // Silent
      }
    };

    pollInvites();
    const interval = setInterval(pollInvites, 5000);
    return () => clearInterval(interval);
  }, [incomingChallenge]);

  const handleRespondChallenge = async (response: 'accept' | 'decline') => {
    if (!incomingChallenge) return;
    
    try {
      setRespondingChallenge(true);
      const shAuth = (window as any).shAuth;
      const ajaxUrl = normalizeAjaxUrl(shAuth?.ajax_url || '/wp-admin/admin-ajax.php');
      const nonce = shAuth?.nonce || '';

      const formData = new FormData();
      formData.append('action', 'sh_respond_battle_invitation');
      formData.append('room_id', incomingChallenge.room_id);
      formData.append('response', response);
      formData.append('nonce', nonce);

      const resRaw = await fetch(ajaxUrl, {
        method: 'POST',
        body: formData
      });
      const res = await resRaw.json();
      
      if (res.success) {
        if (response === 'accept') {
          window.location.href = `${window.location.origin}/dashboard/chorcaghor/chapter-wise/?battle=${incomingChallenge.room_id}&subject=${incomingChallenge.subject_id}&chapter=${incomingChallenge.chapter_id}`;
        } else {
          setIncomingChallenge(null);
        }
      } else {
        alert(res.data || 'আমন্ত্রণ প্রত্যুত্তর ব্যর্থ হয়েছে।');
        setIncomingChallenge(null);
      }
    } catch {
      alert('নেটওয়ার্ক সংযোগ ব্যর্থ হয়েছে।');
    } finally {
      setRespondingChallenge(false);
    }
  };

  // Futuristic Material 3 Dark Mode state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) return stored === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Toggle .dark-mode-only class on <html> element
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark-mode-only');
      localStorage.setItem('darkMode', 'true');
    } else {
      document.documentElement.classList.remove('dark-mode-only');
      localStorage.setItem('darkMode', 'false');
    }
  }, [isDarkMode]);

  // Synchronize initial history state on mount and strip cache-busters
  useEffect(() => {
    const url = new URL(window.location.href);
    
    // Check if user landed on /login, /register, or has login/register query params
    const isLogin = url.pathname.includes('/login') || url.searchParams.has('login');
    const isRegister = url.pathname.includes('/register') || url.searchParams.has('register');

    if (isLogin || isRegister) {
      setIsAuthModalOpen(true);
      // Clean up the URL to the root path without refreshing
      url.searchParams.delete('login');
      url.searchParams.delete('register');
      window.history.replaceState(window.history.state, '', '/' + url.search + url.hash);
    }

    // Clear cache-busting parameters from URL if present
    if (url.searchParams.has('t') || url.searchParams.has('logged_out')) {
      url.searchParams.delete('t');
      url.searchParams.delete('logged_out');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    }

    // Verify auth status on load to block premature mounts
    const isWp = typeof window !== 'undefined' && !!(window as any).shAuth;
    const hasWpUser = isWp ? !!(window as any).shAuth?.user : (localStorage.getItem('isAuthenticated') === 'true');
    setIsAuthenticated(hasWpUser);
    localStorage.setItem('isAuthenticated', hasWpUser ? 'true' : 'false');

    if (hasWpUser && !window.history.state) {
      let path = getPathFromView(activeView, practiceSubView);
      if (practiceSubView === 'user') {
        const params = new URLSearchParams(window.location.search);
        const idVal = params.get('id');
        if (idVal) {
          path += `?id=${idVal}`;
        }
      }
      window.history.replaceState({
        view: activeView,
        subView: practiceSubView,
        selectedCourseId
      }, '', path);
    }

    setIsAuthChecking(false);
  }, []);

  // Listen to popstate event (browser back/forward button clicks)
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state;
      if (state) {
        if (state.view && Object.keys(viewComponents).includes(state.view)) {
          setActiveView(state.view);
        }
        if (state.subView) {
          const validSubViews = ['main', 'chapterWise', 'mockTests', 'questionBanks', 'schoolQuestionBanks', 'history', 'user'];
          if (validSubViews.includes(state.subView)) {
            setPracticeSubView(state.subView);
          }
        }
        setSelectedCourseId(state.selectedCourseId !== undefined ? state.selectedCourseId : null);
      } else {
        const parsed = parsePathname(window.location.pathname);
        setActiveView(parsed.view);
        setPracticeSubView(parsed.subView);
        setSelectedCourseId(null);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Sync state changes to browser history
  useEffect(() => {
    if (!isAuthenticated) return;
    const current = window.history.state;
    const isDifferent = !current ||
      current.view !== activeView ||
      current.subView !== practiceSubView ||
      current.selectedCourseId !== selectedCourseId;

    if (isDifferent) {
      let path = getPathFromView(activeView, practiceSubView);
      if (practiceSubView === 'user') {
        const params = new URLSearchParams(window.location.search);
        const idVal = params.get('id');
        if (idVal) {
          path += `?id=${idVal}`;
        }
      }
      window.history.pushState({
        view: activeView,
        subView: practiceSubView,
        selectedCourseId
      }, '', path);
    }
  }, [activeView, practiceSubView, selectedCourseId]);

  const handleViewChange = (view: string, subView: string = 'main') => {
    setActiveView(view as ViewType);
    setPracticeSubView(subView as any);
    setSelectedCourseId(null);
  };

  const [activePopupAd, setActivePopupAd] = useState<any>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchPopupAds = async () => {
      try {
        const response = await fetch('/wp-json/shuddhoshikha/v1/syllabus-ads');
        const data = await response.json();
        if (data.success && Array.isArray(data.ads)) {
          const popupAd = data.ads.find((ad: any) => {
            if (ad.placement !== 'popup_login') return false;
            if (ad.device === 'desktop' && isMobile) return false;
            if (ad.device === 'mobile' && !isMobile) return false;
            
            // Check if shown in this session
            const shownKey = `sh_ad_shown_${ad.id}`;
            if (sessionStorage.getItem(shownKey) === 'true') return false;
            
            return true;
          });
          
          if (popupAd) {
            setActivePopupAd(popupAd);
          }
        }
      } catch (err) {
        console.error('Failed to load popup ads:', err);
      }
    };

    fetchPopupAds();
  }, [isAuthenticated, isMobile]);

  const handleClosePopup = () => {
    if (activePopupAd) {
      sessionStorage.setItem(`sh_ad_shown_${activePopupAd.id}`, 'true');
      setActivePopupAd(null);
    }
  };

  // Global Student Report Modal State
  const [globalReportingQuestionId, setGlobalReportingQuestionId] = useState<number | null>(null);
  const [globalReportCategory, setGlobalReportCategory] = useState<string>('প্রশ্নে ডাউট');
  const [globalReportComment, setGlobalReportComment] = useState<string>('');
  const [isGlobalSubmittingReport, setIsGlobalSubmittingReport] = useState<boolean>(false);
  const [globalReportError, setGlobalReportError] = useState<string | null>(null);
  const [globalReportSuccess, setGlobalReportSuccess] = useState<string | null>(null);

  useEffect(() => {
    (window as any).showReportModal = (questionId: number) => {
      setGlobalReportingQuestionId(questionId);
      setGlobalReportCategory('প্রশ্নে ডাউট');
      setGlobalReportComment('');
      setGlobalReportError(null);
      setGlobalReportSuccess(null);
    };
    return () => {
      delete (window as any).showReportModal;
    };
  }, []);

  const handleGlobalReportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGlobalReportError(null);
    setGlobalReportSuccess(null);

    if (globalReportComment.trim().length < 10) {
      setGlobalReportError('মন্তব্য অন্তত ১০ অক্ষরের হতে হবে।');
      return;
    }

    setIsGlobalSubmittingReport(true);

    const nonce = (window as any).shAuth?.rest_nonce || '';

    try {
      const response = await fetch('/wp-json/shuddhoshikha/v1/submit-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(nonce ? { 'X-WP-Nonce': nonce } : {}),
        },
        body: JSON.stringify({
          question_id: globalReportingQuestionId,
          report_category: globalReportCategory,
          comment: globalReportComment,
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setGlobalReportSuccess('রিপোর্ট সফলভাবে জমা দেওয়া হয়েছে।');
        
        // Dispatch custom event to notify all listeners
        window.dispatchEvent(new CustomEvent('question-flagged', { 
          detail: { questionId: globalReportingQuestionId } 
        }));

        setTimeout(() => {
          setGlobalReportingQuestionId(null);
          setGlobalReportComment('');
          setGlobalReportSuccess(null);
        }, 1500);
      } else {
        setGlobalReportError(data.message || 'রিপোর্ট জমা দিতে ব্যর্থ হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।');
      }
    } catch (err) {
      setGlobalReportError('সার্ভার সংযোগে সমস্যা হয়েছে। আবার চেষ্টা করুন।');
    } finally {
      setIsGlobalSubmittingReport(false);
    }
  };

  if (isAuthChecking) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-[#00CBA9]" />
          <p className="font-bangla text-xs text-gray-400">লোডিং হচ্ছে, অনুগ্রহ করে অপেক্ষা করুন...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname;
      const segments = path.replace(/^\/|\/$/g, '').split('/').filter(Boolean);
      const dashboardIndex = segments.indexOf('dashboard');
      if (dashboardIndex !== -1) {
        const homeUrl = (window as any).shAuth?.home_url || '/';
        window.location.href = homeUrl;
        return null;
      }
    }

    return (
      <div className="min-h-screen bg-[#080808]">
        <LandingPage onLoginClick={() => {
          const homeUrl = (window as any).shAuth?.home_url || '/';
          window.location.href = `${homeUrl.replace(/\/$/, '')}/auth/register/`;
        }} onBecomeInstructor={() => setIsInstructorRegOpen(true)} />
        <AuthModal 
          isOpen={isAuthModalOpen} 
          onClose={() => setIsAuthModalOpen(false)} 
          onLoginSuccess={(redirectUrl?: string) => {
            setIsAuthChecking(true); // Lock screen immediately on login success to prevent layout flashing
            localStorage.setItem('isAuthenticated', 'true');
            if (redirectUrl) {
              window.location.href = redirectUrl;
            } else {
              window.location.reload();
            }
          }} 
        />
        <InstructorRegistrationModal
          isOpen={isInstructorRegOpen}
          onClose={() => setIsInstructorRegOpen(false)}
        />
      </div>
    );
  }

  const isBottomNavVisible = (activeView !== 'চর্চাঘর' || practiceSubView === 'main') && selectedCourseId === null;

  const ActiveComponent = viewComponents[activeView] as React.ComponentType<any>;

  return (
    <div className={`min-h-screen bg-[#F4F7FB] dark:bg-brand-dark-bg font-bangla transition-colors duration-200 ${isDarkMode ? 'dark-mode-only' : ''}`}>
      {/* Desktop Sidebar */}
      <Sidebar 
        activeView={activeView} 
        onViewChange={(view) => handleViewChange(view as ViewType)} 
        isDarkMode={isDarkMode} 
        onDarkModeToggle={() => setIsDarkMode(!isDarkMode)}
        subscriptionTier={subscriptionTier}
        onLogout={handleLogout}
      />

      {/* Mobile Top Bar */}
      <MobileHeader 
        onViewChange={(view) => handleViewChange(view as ViewType)} 
        isDarkMode={isDarkMode} 
        onDarkModeToggle={() => setIsDarkMode(!isDarkMode)} 
        onNotificationOpen={() => setIsNotificationOpen(true)} 
        subscriptionTier={subscriptionTier}
        unreadCount={unreadCount}
      />

      {/* Main Content Area */}
      <main className="ml-0 lg:ml-[280px] min-h-screen">
        <div className={`max-w-7xl mx-auto pt-20 pb-28 lg:p-8 ${
          activeView === 'হোম' && isMobile
            ? 'px-0'
            : 'px-3 sm:px-4'
        }`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeView}
              initial={{ opacity: 0, x: 15, scale: 0.985 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -15, scale: 0.985 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            >
              {activeView === 'চর্চাঘর' ? (
                <PracticeView
                  subView={practiceSubView}
                  onSubViewChange={setPracticeSubView}
                  isDarkMode={isDarkMode}
                  subscriptionTier={subscriptionTier}
                  onViewChange={handleViewChange}
                />
              ) : (
                <ActiveComponent 
                  onViewChange={handleViewChange}
                  selectedCourseId={selectedCourseId}
                  setSelectedCourseId={setSelectedCourseId}
                  onSelectCourse={(courseId: number | null) => {
                    setSelectedCourseId(courseId);
                    setActiveView('কোর্সসমূহ');
                  }}
                  isDarkMode={isDarkMode}
                  onDarkModeToggle={() => setIsDarkMode(!isDarkMode)}
                  onNotificationOpen={() => setIsNotificationOpen(true)}
                  subscriptionTier={subscriptionTier}
                  onUpgradeSuccess={(tier: 'free' | 'pro' | 'max') => setSubscriptionTier(tier)}
                  onLogout={handleLogout}
                  unreadCount={unreadCount}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      {isBottomNavVisible && (
        <MobileBottomNav activeView={activeView} onViewChange={(view) => handleViewChange(view as ViewType)} isDarkMode={isDarkMode} />
      )}

      {/* Slide-over Notification Sidebar */}
      <NotificationSidebar 
        isOpen={isNotificationOpen} 
        onClose={() => setIsNotificationOpen(false)} 
        isDarkMode={isDarkMode} 
        notifications={notifications}
        notices={notices}
        markAsRead={markAsRead}
      />

      {/* GLOBAL STUDENT REPORT MODAL OVERLAY */}
      {globalReportingQuestionId !== null && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white dark:bg-[#1E293B] border border-gray-100 dark:border-white/10 rounded-[28px] shadow-2xl w-full max-w-[620px] overflow-hidden p-8 relative">
            
            {/* Close Button */}
            <button 
              onClick={() => setGlobalReportingQuestionId(null)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 bg-transparent border-0 cursor-pointer focus:outline-none"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>

            <h3 className="font-bangla text-xl font-bold text-gray-900 dark:text-gray-100 m-0 mb-6 text-left">
              প্রশ্ন রিপোর্ট করো
            </h3>

            <form onSubmit={handleGlobalReportSubmit} className="space-y-6">
              {/* Category Options Grid */}
              <div className="flex flex-wrap gap-2.5">
                {[
                  'প্রশ্নে ডাউট',
                  'অসম্পূর্ণ প্রশ্ন',
                  'ভুল উত্তর',
                  'ভুল ক্যাটাগরি',
                  'অসম্পূর্ণ সলিউশন'
                ].map((category) => {
                  const isCatSelected = globalReportCategory === category;
                  return (
                    <button
                      key={category}
                      type="button"
                      onClick={() => setGlobalReportCategory(category)}
                      className={`font-bangla text-sm py-2 px-4 rounded-xl transition-all duration-200 border-0 cursor-pointer focus:outline-none ${
                        isCatSelected 
                          ? 'bg-[#006A4E] text-white font-semibold' 
                          : 'bg-[#F1F3F5] dark:bg-white/5 text-[#1F1F1F] dark:text-slate-350 hover:bg-gray-250 dark:hover:bg-white/10'
                      }`}
                    >
                      {category}
                    </button>
                  );
                })}
              </div>

              {/* Comment Input */}
              <div className="space-y-2 text-left">
                <label className="font-bangla text-sm font-semibold text-gray-750 dark:text-slate-300 block">
                  মন্তব্য
                </label>
                <textarea
                  value={globalReportComment}
                  onChange={(e) => setGlobalReportComment(e.target.value)}
                  placeholder="অন্তত ১০ অক্ষরের মন্তব্য লিখো"
                  className="font-bangla w-full h-28 p-4 bg-white dark:bg-slate-900 border border-gray-200 dark:border-white/10 rounded-2xl resize-none text-sm focus:outline-none focus:ring-2 focus:ring-[#00C9A7]/35 dark:text-white"
                />
              </div>

              {/* Status / Error / Success Messages */}
              {globalReportError && (
                <p className="font-bangla text-sm text-red-500 font-medium m-0 text-left">
                  {globalReportError}
                </p>
              )}
              {globalReportSuccess && (
                <p className="font-bangla text-sm text-[#00C9A7] font-semibold m-0 text-left">
                  {globalReportSuccess}
                </p>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isGlobalSubmittingReport || !!globalReportSuccess}
                className="font-bangla w-full py-4 text-base font-semibold rounded-2xl border-0 text-white bg-[#8BB29E] hover:bg-[#7aa08c] disabled:opacity-60 cursor-pointer focus:outline-none transition-all duration-200 text-center"
              >
                {isGlobalSubmittingReport ? 'প্রক্রিয়াধীন...' : 'রিপোর্ট'}
              </button>
            </form>
          </div>
        </div>
      )}
      {/* SECURE LOGOUT CONFIRMATION MODAL OVERLAY */}
      {isLogoutModalOpen && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm transition-all duration-300">
          <div className="bg-[#1E1E1E] border border-white/10 rounded-[28px] shadow-2xl w-full max-w-[420px] overflow-hidden p-8 relative text-center">
            
            {/* Close Button */}
            <button 
              onClick={() => setIsLogoutModalOpen(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-white bg-transparent border-0 cursor-pointer focus:outline-none transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>

            {/* Centered Circular Icon */}
            <div className="mx-auto w-16 h-16 rounded-full bg-black/30 border border-white/5 flex items-center justify-center mb-6">
              <div className="w-10 h-10 rounded-full bg-[#E53935]/10 flex items-center justify-center text-[#E53935]">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
                </svg>
              </div>
            </div>

            <h3 className="font-bangla text-xl font-bold text-white m-0 mb-2">
              কনফর্ম
            </h3>
            <p className="font-bangla text-sm text-[#A0A5AD] m-0 mb-8">
              তুমি কি লগআউট করতে চাও ?
            </p>

            <div className="grid grid-cols-2 gap-3.5">
              <button
                type="button"
                onClick={() => setIsLogoutModalOpen(false)}
                className="font-bangla py-3.5 text-sm font-semibold rounded-2xl border-0 text-white bg-white/5 hover:bg-white/10 cursor-pointer focus:outline-none transition-all duration-200 text-center"
              >
                ক্যান্সেল
              </button>
              <button
                type="button"
                onClick={handleConfirmLogout}
                disabled={isLoggingOut}
                className="font-bangla py-3.5 text-sm font-semibold rounded-2xl border-0 text-white bg-[#E53935] hover:bg-[#D32F2F] disabled:opacity-60 cursor-pointer focus:outline-none transition-all duration-200 text-center flex items-center justify-center gap-2"
              >
                {isLoggingOut ? (
                  <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                ) : (
                  'লগআউট'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Login Popup Promotion Modal */}
      <AnimatePresence>
        {activePopupAd && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 dark:bg-black/80 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={isMobile ? { y: '100%' } : { scale: 0.95, opacity: 0 }}
              animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
              exit={isMobile ? { y: '100%' } : { scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`bg-white dark:bg-[#1E1E1E] shadow-2xl overflow-hidden border border-slate-100 dark:border-white/5 flex flex-col justify-between ${
                isMobile 
                  ? 'fixed inset-0 w-full h-full rounded-none' 
                  : 'w-[420px] rounded-3xl max-h-[90vh]'
              }`}
            >
              <div>
                {/* Close Button on Top Corner */}
                <button
                  onClick={handleClosePopup}
                  className="absolute top-4 right-4 z-50 w-8 h-8 rounded-full bg-black/40 hover:bg-black/65 text-white flex items-center justify-center cursor-pointer border-none shadow-sm active:scale-90 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Banner Image */}
                {activePopupAd.banner_url ? (
                  <img
                    src={activePopupAd.banner_url}
                    alt={activePopupAd.title}
                    className={`w-full object-cover ${isMobile ? 'h-[45vh]' : 'h-[200px]'}`}
                  />
                ) : (
                  <div className={`w-full bg-gradient-to-r from-sky-400 via-blue-600 to-indigo-900 flex items-center justify-center ${isMobile ? 'h-[30vh]' : 'h-[160px]'}`}>
                    <span className="font-bangla font-black text-white text-lg tracking-wide">শুদ্ধশিক্ষা প্রমোশন</span>
                  </div>
                )}

                {/* Text Content */}
                <div className="p-6 md:p-8 space-y-3">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 uppercase tracking-wider">
                    বিশেষ ক্যাম্পেইন
                  </span>
                  <h3 className="font-bangla text-xl md:text-2xl font-black text-slate-800 dark:text-white leading-tight">
                    {activePopupAd.title}
                  </h3>
                  {activePopupAd.description && (
                    <p className="font-bangla text-sm text-slate-500 dark:text-slate-400 leading-relaxed max-w-sm">
                      {activePopupAd.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-6 md:p-8 bg-slate-50 dark:bg-white/[0.02] border-t border-slate-100 dark:border-white/5 flex flex-col gap-2.5">
                <a
                  href={activePopupAd.redirect_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleClosePopup}
                  className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b496] text-slate-950 font-black text-center text-sm rounded-2xl cursor-pointer shadow-lg shadow-[#00CBA9]/15 transition-all border-none no-underline active:scale-[0.99]"
                >
                  বিস্তারিত জানুন
                </a>
                <button
                  onClick={handleClosePopup}
                  className="w-full py-3 bg-transparent hover:bg-slate-100 dark:hover:bg-white/5 text-slate-500 dark:text-slate-400 font-bold text-center text-xs rounded-2xl cursor-pointer transition-all border-none"
                >
                  এখন নয়, পরে দেখবো
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Real-time Challenge Invitation Modal Popup */}
      {incomingChallenge && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#1E1E1E] rounded-3xl p-6 max-w-sm w-full space-y-4 border border-gray-100 dark:border-white/10 shadow-2xl text-center">
            {/* Centered Circular Icon */}
            <div className="mx-auto w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center text-purple-500 animate-pulse">
              <Users className="w-8 h-8" />
            </div>

            <div className="space-y-1.5">
              <h3 className="font-bangla text-lg font-extrabold text-[#1F1F1F] dark:text-white">ব্যাটল চ্যালেঞ্জ!</h3>
              <p className="font-bangla text-xs text-gray-500 dark:text-gray-400">
                <span className="font-bold text-purple-500">{incomingChallenge.sender_name}</span> আপনাকে একটি লাইভ ব্যাটেলে আমন্ত্রণ জানিয়েছেন!
              </p>
              <div className="inline-block px-3 py-1 rounded-full bg-slate-100 dark:bg-white/5 font-bangla text-[10px] font-bold text-slate-700 dark:text-gray-300 mt-2">
                বিষয়: {incomingChallenge.subject_id === 'sub_bn1' ? 'বাংলা ১ম পত্র' : incomingChallenge.subject_id}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2">
              <button
                type="button"
                onClick={() => handleRespondChallenge('decline')}
                disabled={respondingChallenge}
                className="font-bangla py-3 text-xs font-bold rounded-2xl bg-gray-150 hover:bg-gray-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-white border-none cursor-pointer disabled:opacity-50"
              >
                প্রত্যাখ্যান
              </button>
              <button
                type="button"
                onClick={() => handleRespondChallenge('accept')}
                disabled={respondingChallenge}
                className="font-bangla py-3 text-xs font-extrabold rounded-2xl bg-[#00CBA9] hover:bg-[#00B097] text-white border-none cursor-pointer shadow-lg shadow-emerald-500/15 flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {respondingChallenge ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    <Play className="w-3 h-3 fill-current" />
                    <span>খেলুন</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

